import React, { useEffect, useState } from 'react';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  getAllUsersAsync,
  SelectCurrentUser,
  SelectUpdateFlag,
  SelectUsersList,
  setAddUserModalShow,
  setPhoneNumberUpdateModalShow,
  setUpdateFlag,
  updateUserActiveStatusAsync,
  updateUserMfaStatusAsync
} from '../features/authentication/authenticationSlice';
import { changeDateFormat, tableDesign, userInterface } from '../app/consts';
import RegisterUser from './RegisterUser';
import UpdatePhoneNumberModal from './UpdatePhoneNumberModal';

const ManageUsers = () => {
  const dispatch = useAppDispatch();
  const usersList = useAppSelector(SelectUsersList);
  const updateFlag = useAppSelector(SelectUpdateFlag);
  const userConnected = useAppSelector(SelectCurrentUser);

  const [selectedUser, setSelectedUser] = useState<userInterface | {}>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    dispatch(getAllUsersAsync());
    // eslint-disable-next-line
  }, [updateFlag]);

  const handleActivationStatusChange = (username: string) => {
    dispatch(updateUserActiveStatusAsync(username))
      .unwrap()
      .then(() => {
        dispatch(setUpdateFlag());
      })
      .catch(() => { });
  };

  const handleMfaStatusChange = (username: string) => {
    dispatch(updateUserMfaStatusAsync(username))
      .unwrap()
      .then(() => {
        dispatch(setUpdateFlag());
      })
      .catch(() => { });
  };

  return (
    <>
      <div style={{ margin: 'auto', textAlign: 'center', marginTop: '2%' }}>
        <h1>ניהול משתמשים</h1>
        <Button
          onClick={() => dispatch(setAddUserModalShow(true))}
          variant="contained"
          size='large'
          color='inherit'
        >
          הוסף משתמש חדש
        </Button>
        <br /><br /><br />

        {usersList.length === 0 ? (
          <img
            src='/FadingCircles.gif'
            alt='spinner'
            style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          />
        ) : (
          <Paper sx={tableDesign}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="sticky table"
                sx={{ "& .MuiTableCell-root": { fontSize: "1em", textAlign: 'center' } }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>שם משתמש</TableCell>
                    <TableCell>שם פרטי</TableCell>
                    <TableCell>שם משפחה</TableCell>
                    <TableCell>דואר אלקטרוני</TableCell>
                    <TableCell>מספר טלפון</TableCell>
                    <TableCell>פעיל?</TableCell>
                    <TableCell>תאריך התחברות אחרונה</TableCell>
                    <TableCell colSpan={2}>פעולות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersList
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    ?.map((user: userInterface, index: number) => {
                      const isCurrentUser = user.email === userConnected?.email || user.username === userConnected?.email;
                      const isSuperUser = Boolean(userConnected?.isSU);

                      return (
                        <TableRow key={user.username || index}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.first_name}</TableCell>
                          <TableCell>{user.last_name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.phone_num}{' '}
                            {(isCurrentUser || isSuperUser) && (
                              <Button
                                onClick={() => {
                                  setSelectedUser(user);
                                  dispatch(setPhoneNumberUpdateModalShow(true));
                                }}
                              >
                                עדכן
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>{user.is_active ? "כן" : "לא"}</TableCell>
                          <TableCell>
                            {user.last_login === null
                              ? "לא ביצע התחברות"
                              : `${changeDateFormat(user.last_login as string)} - ${new Date(user.last_login as string).toLocaleString('he-il').slice(11, 16)}`}
                          </TableCell>
                          <TableCell>
                            {isCurrentUser ? (
                              "אין אפשרות לבטל משתמש מחובר"
                            ) : (
                              <Button onClick={() => handleActivationStatusChange(user.username)}>
                                {user.is_active ? "בטל משתמש" : "הפעל משתמש"}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            {isCurrentUser ? (
                              <Button onClick={() => handleMfaStatusChange(user.username)}>
                                {user.mfa_enabled ? "בטל אימות דו שלבי" : "הפעל אימות דו שלבי"}
                              </Button>
                            ) : isSuperUser ? (
                              <Button onClick={() => handleMfaStatusChange(user.username)}>
                                {user.mfa_enabled ? "בטל אימות דו שלבי" : "הפעל אימות דו שלבי"}
                              </Button>
                            ) : (
                              "אין אפשרות לשנות עבור משתמש אחר"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[3, 6, 12]}
              component="div"
              count={usersList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ justifySelf: 'right', direction: 'ltr' }}
            />
          </Paper>
        )}
      </div>
      <RegisterUser />
      <UpdatePhoneNumberModal user={selectedUser} />
    </>
  );
};

export default ManageUsers;